/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { PropsWithChildren } from "react";
import { makeStyles } from "@material-ui/core";
import ExtensionIcon from "@material-ui/icons/Extension";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import HomeIcon from "@material-ui/icons/Home";
import LibraryBooks from "@material-ui/icons/LibraryBooks";
import SearchIcon from "@material-ui/icons/Search";
import GroupsIcon from "@material-ui/icons/Group";
import VisibilityIcon from "@material-ui/icons/VisibilityOutlined";
import LogoFull from "./LogoFull";
import LogoIcon from "./LogoIcon";
import { NavLink } from "react-router-dom";
import { Settings as SidebarSettings } from "@backstage/plugin-user-settings";
import {
  Link,
  Sidebar,
  SidebarDivider,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  sidebarConfig,
  useSidebarOpenState,
} from "@backstage/core-components";
import { Shortcuts } from "@backstage/plugin-shortcuts";
import { MyGroupsSidebarItem } from "@backstage/plugin-org";
import { GroupIcon } from "@backstage/core-components";
import { AdminIcon, OpsgenieIcon } from "../icons";
import { useIsAdmin } from "@internal/plugin-admin-panel";

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: "flex",
    flexFlow: "row nowrap",
    alignItems: "center",
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link
        component={NavLink}
        to="/"
        underline="none"
        className={classes.link}
      >
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const isAdmin = useIsAdmin();

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarItem
          icon={SearchIcon}
          to="search?query=&types[]=software-catalog"
          text="Advanced Search"
        />
        <Shortcuts />
        {/* @ts-ignore */}
        <SidebarDivider />
        {/* Global nav, not org-specific */}
        <SidebarItem
          icon={HomeIcon}
          to="catalog?filters[kind]=component&filters[user]=all"
          text="Home"
        />
        <MyGroupsSidebarItem
          singularTitle="My Team"
          pluralTitle="My Teams"
          icon={GroupIcon}
        />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={FileCopyIcon} to="create" text="Create..." />
        {/* End global nav */}
        {/* @ts-ignore */}
        <SidebarDivider />
        {/* @ts-ignore */}
        <SidebarScrollWrapper>
          <SidebarItem icon={OpsgenieIcon} to="/opsgenie" text="OpsGenie" />
          <SidebarItem
            icon={VisibilityIcon}
            to="/ops-review"
            text="Ops Review"
          />
          {isAdmin && (
            <SidebarItem
              icon={AdminIcon}
              to="/admin-panel"
              text="Admin Panel"
            />
          )}
          {isAdmin && (
            <SidebarItem
              icon={GroupsIcon}
              to="/ownership-editor"
              text="Ownership Editor"
            />
          )}
        </SidebarScrollWrapper>
        {/* @ts-ignore */}
        <SidebarSpace />
        {/* @ts-ignore */}
        <SidebarDivider />
        <SidebarSettings />
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
